import React from 'react';
import { take } from 'lodash';
import { IThumbnail, PoiId, PoiType } from 'utils/entities';
import Share from 'components/navigation/share';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import * as Styled from './styled';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { ImageItem } from './image-item';
import { useTheme } from 'hooks/useTheme';
import GridIcon from 'assets/svg/grid.svg';
import LocationIcon from 'assets/svg/location-gallery-2.svg';
import { ShareButtonSource } from 'components/navigation/share/ShareButton';
import AllPhotosListModal from 'components/unit-page/new-gallery/all-photos-list';
import { useLocale } from 'locale';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { DEFAULT_PLACEHOLDER_IMAGE_URL } from 'utils/gallery';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { routeSelector, prevRouteSelector } from 'store/state/selectors/router';
import { navigateTo } from 'store/state/router/actions';
import { ImageGallery } from '../ImageGallery';
import { Text } from 'ds/components/typography';
import { CleanButton } from 'ds/components/button';
import EditBulletinButton from 'components/edit-bulletin-button';
import ManageMenu from 'components/manage-bulletin/self-contained';
import { useRoute, Route } from 'config/routes';
import { isUnitPageRoute } from 'utils/marketplaceRoutes';
import { isServer } from 'utils';
import withEvents from 'analytics/withEvents';
import { IMAGE_PIXEL_DENSITY } from 'components/popup-image-gallery/constants';
import { rangeToGalleryHeight } from '../config';

interface EventViewPhotoGalleryOpenProps {
  image: IThumbnail;
  index: number;
  total: number;
}

interface ImagesContainerProps {
  images: IThumbnail[];
  fullScreen?: boolean;
  showEditBtn?: boolean;
  showManageBulletinMenu?: boolean;
  altText?: string;
  showFavoritesBtn: boolean;
  id: PoiId;
  enableShare: boolean;
  type: PoiType;
  shareSource?: ShareButtonSource;
  selectedImageIdx: number | null;
  setSelectedImageIdx: (idx: number | null) => void;
  eventViewPhotoGalleryOpen?: (opts: EventViewPhotoGalleryOpenProps) => void;
  showMapViewToggler?: boolean;
  openMap?: () => void;
}

function createCopyText(routeName: Route, id: string, builder: (name: string, params?: { [key: string]: any }) => string) {
  if (isServer()) return null;

  const copyLink = isUnitPageRoute(routeName) ? builder(routeName, { id }) : null;
  return (window.location.origin + copyLink);
}

const ImagesContainerImpl: React.FC<ImagesContainerProps> = (props) => {
  const {
    images,
    showFavoritesBtn,
    id,
    enableShare,
    altText,
    fullScreen,
    type,
    showEditBtn,
    showManageBulletinMenu,
    shareSource,
    selectedImageIdx,
    setSelectedImageIdx,
    showMapViewToggler,
    openMap,
    eventViewPhotoGalleryOpen,
  } = props;

  const breakpointValue = useScreenBreakpoint();
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const { borderRadius } = useTheme();
  const [ sliderIdx, setSliderIdx ] = React.useState<number>(0);
  const { router, route } = useRoute();

  const copyText = createCopyText(route.name, id, router.buildUrl);
  const height = rangeToGalleryHeight[breakpointValue] * IMAGE_PIXEL_DENSITY;
  const imagesToShow = take(images, Math.min(images.length, Styled.MAX_IMAGES_LAYOUT_COUNT));
  const count = images.length;
  const isEmptyGallery = count === 0;

  const handleClick = (imagesList: IThumbnail[], idx: number) => {
    setSelectedImageIdx(idx);
    eventViewPhotoGalleryOpen({
      image: imagesList[idx],
      index: idx,
      total: imagesList.length,
    });
  };

  const content = fullScreen ? (
    <Styled.ImageGridFullScreen>
      <ImageGallery
        images={images}
        activeSlide={sliderIdx}
        handleSlideIndexChange={setSliderIdx}
        openFullscreen={handleClick}
        altText={altText}
        height={height}
      />
    </Styled.ImageGridFullScreen>
  ) : (
    <Styled.ImageGrid>
      {imagesToShow.map((image, i) => {
        return (
          <ImageItem
            key={`${image.url}-${i}`}
            url={image.url}
            height={height}
            rotateDegrees={image.rotateDegrees}
            altText={altText}
            handleClick={() => handleClick(images, i)}
            style={Styled.getLayoutCssProps(images.length, i, borderRadius.small)}
          />
        );
      })}
    </Styled.ImageGrid>
  );

  const bottomLeftContent = fullScreen ? (
    <Styled.MiddleLegend>
      <Text weight="bold">{t('unitPage.gallery.all.fullscreen.photos.count', { count, sliderIdx: sliderIdx + 1 })}</Text>
    </Styled.MiddleLegend>
  ) : (
    <Styled.AllImageButton onClick={() => handleClick(images, 0)}>
      <GridIcon />
      {t('unitPage.gallery.all.photos', { count })}
    </Styled.AllImageButton>
  );

  const manageBulletinMenuButtonPosition = showEditBtn ? 'right' : 'left';
  const manageBulletinMenuButton = id && showManageBulletinMenu ? (
    <ManageMenu
      isRound
      dropdownPosition={manageBulletinMenuButtonPosition === 'left' ? 'bottom-right' : 'botton-left'}
    />
  ) : null;

  return (
    <>
      {selectedImageIdx === null ? (
        <Styled.Root>
          {isEmptyGallery ? <Styled.PlaceholderImage imageUrl={constructImageURI(DEFAULT_PLACEHOLDER_IMAGE_URL)} /> : content}
          <Styled.TopLeftControls>
            {enableShare ? <Share tooltip={false} isRound source={shareSource}/> : null}
            {id && showFavoritesBtn ? <FavoritesToggler id={id} type={type} tooltip={false} withButton noText isRound /> : null}
            {manageBulletinMenuButtonPosition === 'left' ? manageBulletinMenuButton : null}
            {id && showEditBtn ? <EditBulletinButton ignoreMobileStyles id={id} isRound/> : null}
          </Styled.TopLeftControls>
          <Styled.TopRightControls>
            {manageBulletinMenuButtonPosition === 'right' ? manageBulletinMenuButton : null}
          </Styled.TopRightControls>
          <Styled.BottomLeftControls style={fullScreen ? { right: 0 } : {}}>
            {isEmptyGallery ? null : bottomLeftContent}
          </Styled.BottomLeftControls>
          <Styled.BottomRightControls>
            {showMapViewToggler && fullScreen ? (
              <CleanButton data-auto="map-button" onlyIcon mode="secondaryNegative" onClick={openMap}>
                <LocationIcon width={24} height={24} />
              </CleanButton>
            ) : null}
          </Styled.BottomRightControls>
        </Styled.Root>
      ) : null}
      <AllPhotosListModal
        onClose={() => setSelectedImageIdx(null)}
        copyText={copyText}
        selectedImageIdx={selectedImageIdx}
        images={images}
        id={id}
      />
    </>
  );
};

const mapStateToProps = (state: State) => ({
  route: routeSelector(state),
  prevRoute: prevRouteSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
};


const withEventsImagesContainerImpl = withEvents<ImagesContainerProps>((sendEvent) => ({
  eventViewPhotoGalleryOpen({ image, index, total }) {
    const isFloorPlanPhoto = Boolean(image.isFloorPlan);
    const eventAction = isFloorPlanPhoto ? 'property_floor_plan_open' : 'property_view_photo_gallery_open';
    sendEvent(eventAction, 'property', {
      event: {
        image: {
          description: image.description,
          image_url: image.url,
          isFloorPlanPhoto,
        },
        image_index: index,
        index_total: total,
      },
    });
  },
}))(ImagesContainerImpl);

export const ImagesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  ({ route, prevRoute, ...stateProps }, { navigate, ...dispatchProps }, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    selectedImageIdx: route.params.activeListingImage !== undefined ? route.params.activeListingImage : null,
    setSelectedImageIdx: (idx: number | null) => {
      if (idx !== null) {
        navigate(route.name, { ...route.params, activeListingImage: idx });
      }
      else if (prevRoute && prevRoute.name === route.name && route.params.activeListingImage !== undefined && prevRoute.params.activeListingImage === undefined) {
        window.history.back();
      }
      else {
        navigate(route.name, { ...route.params, activeListingImage: undefined });
      }
    },
  })
)(withEventsImagesContainerImpl);
